import React, { useState, useEffect } from 'react';
import { Trans } from '@lingui/react/macro';
import {
    getResidentialUnitDocumentVersions,
    getDocumentFileType,
    getOpenDocumentUrl,
    getProjectDocumentVersions,
    renameDocument,
    getDownloadDocumentUrl,
} from '@/services/documents';
import theme from './DocumentList.module.scss';
import { LoadingIndicator, ToggleInput } from '@ygtechnologies/bygr-frontend-component-library';
import DocumentRejectDialog from '../DocumentRejectDialog';
import { FileType } from '@ygtechnologies/rubus-frontend-utilities';
import { classnames } from '@/utils/html';
import { DateDisplay } from '@/components/localization';
import { faTimesCircle, faCheckCircle, faLink } from '@fortawesome/pro-light-svg-icons';
import {
    ResidentialUnitDocument,
    ResidentialUnitDocumentVersion,
    DocumentStatus,
    ChangedItem,
    BaseDocument,
    DocumentVersion,
    ProjectDocumentVersion,
} from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useAsyncState, usePrevious } from '@/utils/hooks';
import DocumentApproveDialog from '../DocumentApproveDialog';
import DocumentLinkDialog from '../DocumentLinkDialog';
import { DocumentListMode } from '.';
import DocumentPreview from '../DocumentPreview';
import { Badge, Button, ButtonBar, Card, LightboxSingleImage } from '@ygtechnologies/rubus-ui';
import { useLingui } from '@lingui/react';

type StatusBadgeVariant = 'warning' | 'danger' | 'success';

export interface DocumentListItemDetailsProps {
    document: BaseDocument;
    onChange?: (changes: ChangedItem<BaseDocument>[]) => void;
    mode: DocumentListMode;
    onLoad?: () => void;
}

const DocumentListItemDetails: React.FC<DocumentListItemDetailsProps> = ({ document, mode, onChange, onLoad }) => {
    const { _ } = useLingui();
    const residentialUnitDocument = mode === 'ResidentialUnit' ? (document as ResidentialUnitDocument) : null;
    const residentialUnitId = residentialUnitDocument ? residentialUnitDocument.residentialUnitId : null;

    const [versions, setVersions, reloadVersions] = useAsyncState<DocumentVersion[] | null>(
        () =>
            residentialUnitId
                ? getResidentialUnitDocumentVersions(document.documentId, residentialUnitId)
                : getProjectDocumentVersions(document.documentId),
        [document.documentId, residentialUnitId],
        null
    );

    const [lightboxDocument, setLightboxDocument] = useState<DocumentVersion | null>(null);
    const [rejectDocumentVersionId, setRejectDocumentVersionId] = useState<number | null>(null);
    const [approveDocumentVersionId, setApproveDocumentVersionId] = useState<number | null>(null);
    const [showLinkDialog, setShowLinkDialog] = useState(false);

    const previousVersions = usePrevious(versions);
    useEffect(() => {
        if (onLoad && versions && !previousVersions) {
            onLoad();
        }
    }, [onLoad, versions, previousVersions]);

    const triggerChangeEvent = (data: Partial<ResidentialUnitDocument>) => {
        triggerHandler(onChange, [new ChangedItem(document as BaseDocument, { ...(document as BaseDocument), ...data })]);
    };

    const handleThumbnailClick = (e, version) => {
        if (getDocumentFileType(version) === FileType.Image) {
            setLightboxDocument(version);

            e.preventDefault();
        }
    };

    const handleLightboxClose = () => {
        setLightboxDocument(null);
    };

    const handleRejectDialogAccept = (residentialUnitIds: number[], { reason }) => {
        triggerChangeEvent({ status: DocumentStatus.Rejected, lastVersionRejectReason: reason });
        reloadVersions();
    };

    const handleApproveDialogAccept = (residentialUnitIds: number[]) => {
        triggerChangeEvent({ status: DocumentStatus.Approved });
        reloadVersions();
    };

    const handleLinkDialogAccept = () => {
        triggerChangeEvent({});
        reloadVersions();
    };

    const handleRenameAccept = async (version: DocumentVersion, newValue: string) => {
        await renameDocument(version.versionId, newValue);

        setVersions((v) => ChangedItem.fromMerge(version, { name: newValue }).updateArray(v!));
    };

    const renderProjectButtons = () => {
        // const hasPending = (document as ProjectDocument).linkedUnits.some((x) => x.status === DocumentStatus.Pending);
        return (
            <>
                <Button variant="secondary" icon={faLink} onClick={() => setShowLinkDialog(true)}>
                    <Trans>Link / unlink</Trans>
                </Button>
                <DocumentLinkDialog
                    show={showLinkDialog}
                    documentVersionId={document.lastVersionId}
                    onClose={() => setShowLinkDialog(false)}
                    onAccept={handleLinkDialogAccept}
                />
            </>
        );
    };

    const renderVersionButtons = (version: DocumentVersion) => {
        const isPending =
            (version as ResidentialUnitDocumentVersion).status === DocumentStatus.Pending ||
            (version as ProjectDocumentVersion).hasPendingLinks;

        if (!(document as BaseDocument).permissions.canApprove || !isPending) {
            if (mode === 'Project') {
                return null;
            }

            const status = (version as ResidentialUnitDocumentVersion).status;
            const variantMap: { [key in DocumentStatus]: StatusBadgeVariant } = {
                Pending: 'warning',
                Rejected: 'danger',
                Approved: 'success',
            };

            return (
                <div className={theme.status}>
                    <Badge
                        variant={variantMap[status]}
                        appearance="solid"
                        radius="full"
                        size="lg"
                        className={theme.statusBadge}>
                        {_({ id: `DocumentStatus.${status}` })}
                    </Badge>
                </div>
            );
        }

        return (
            <>
                <div className={theme.buttons}>
                    <Button
                        size="xs"
                        variant="success"
                        icon={faCheckCircle}
                        onClick={() => {
                            setApproveDocumentVersionId(version.versionId);
                        }}>
                        <Trans>Publish</Trans>
                    </Button>
                    <Button
                        size="xs"
                        variant="danger"
                        icon={faTimesCircle}
                        onClick={() => {
                            setRejectDocumentVersionId(version.versionId);
                        }}>
                        <Trans>Reject</Trans>
                    </Button>
                </div>

                <DocumentApproveDialog
                    hiddenFromBuyer={document.hideFromBuyer}
                    show={version.versionId === approveDocumentVersionId}
                    documentVersionId={version.versionId}
                    residentialUnitId={residentialUnitDocument ? residentialUnitDocument.residentialUnitId : null}
                    onClose={() => setApproveDocumentVersionId(null)}
                    onApprove={handleApproveDialogAccept}
                />

                <DocumentRejectDialog
                    uploadedByName={document.uploadedByName}
                    show={version.versionId === rejectDocumentVersionId}
                    documentVersionId={version.versionId}
                    residentialUnitId={residentialUnitDocument ? residentialUnitDocument.residentialUnitId : null}
                    onClose={() => setRejectDocumentVersionId(null)}
                    onReject={handleRejectDialogAccept}
                />
            </>
        );
    };

    if (!versions) {
        return <LoadingIndicator />;
    }

    const latestApprovedVersion = versions!.find(
        (x) => (x as ResidentialUnitDocumentVersion).status === DocumentStatus.Approved
    );

    return (
        <div className={theme.detailsContainer}>
            <Card className={theme.card}>
                <div className={theme.versionList}>
                    {versions.map((version) => (
                        <div className={theme.versionContainer} key={version.versionId}>
                            <div
                                className={classnames({
                                    [theme.version]: true,
                                    [theme.latest]: latestApprovedVersion === version,
                                })}>
                                <div className={classnames(theme.text, theme.top)}>
                                    {latestApprovedVersion === version ? (
                                        <Trans>Latest version</Trans>
                                    ) : (
                                        <Trans>Version {version.versionNumber}</Trans>
                                    )}
                                </div>

                                <a
                                    href={getOpenDocumentUrl(version.versionId)}
                                    className={theme.thumbnail}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => handleThumbnailClick(e, version)}>
                                    <DocumentPreview value={version} />
                                </a>

                                <div className={classnames(theme.text, theme.bottom)}>
                                    <ToggleInput
                                        iconMode="over"
                                        className={theme.nameInput}
                                        value={version.name}
                                        wrapperClassName={theme.truncate}
                                        onAccept={(e) => handleRenameAccept(version, e.target.value)}
                                    />
                                    {/* <div className={theme.truncate}>{version.name}</div> */}

                                    <div>
                                        <DateDisplay value={version.uploadDate} />
                                    </div>
                                </div>
                            </div>
                            {renderVersionButtons(version)}
                        </div>
                    ))}
                </div>
            </Card>

            <LightboxSingleImage
                image={{
                    src: lightboxDocument ? getDownloadDocumentUrl(lightboxDocument.versionId) : '',
                    thumbnailSrc: lightboxDocument ? getDownloadDocumentUrl(lightboxDocument.versionId) : '',
                    title: lightboxDocument?.name || '',
                    description: lightboxDocument?.description || '',
                    type: 'image',
                }}
                open={!!lightboxDocument}
                setOpen={handleLightboxClose}
            />

            {mode === 'Project' && (document as BaseDocument).permissions.canReplace && (
                <ButtonBar>
                    <ButtonBar.Right>{renderProjectButtons()}</ButtonBar.Right>
                </ButtonBar>
            )}
        </div>
    );
};

export default DocumentListItemDetails;
